import {getAvailabilityOptions, group, onlineClasses, privateGroup} from "conts/profileConst"
import React, {useEffect, useRef, useState} from "react"
import styled from "styled-components"
import {ButtonPrimary} from "ui/base/Button/ButtonStyled"
import {Modal} from "ui/base/modal/Modal"

import {SelectButton} from "ui/base/selectInput/SelectButton"
import {SelectInputVariant} from "ui/base/selectInput/SelectInputVariant"
import {themeColor, themeFontSize} from "ui/theme"
import {calculateTimeDifferenceInMinutes} from "utils"
import {isTimeConflict, isTimeConflictFromRelevantDays} from "../../../../utils/schedule-utils";

const hoursOption = Array.from({length: 12}, (v, i) => {
    const value = (i + 1).toString()
    return {
        label: value,
        value,
    }
})
const minutesOption = Array.from({length: 60}, (v, i) => {
    const value = i.toString().padStart(2, "0")
    return {
        label: value,
        value,
    }
})

const ampmOption = [
    {label: "AM", value: "AM"},
    {label: "PM", value: "PM"},
]

const defaultHour = {
    label: "6",
    value: "6",
}
const defaultMinute = {
    label: "00",
    value: "00",
}

const defaultAmpm = {
    label: "AM",
    value: "AM",
}

export const AddClassHourModal = ({
                                      handleClose,
                                      show,
                                      classType,
                                      onAddClass,
                                      classDuration,
                                      relevantDays,
                                      selectedDay,
                                      onlineType
                                  }) => {
    const isGroupClass = classType === group
    const isPrivateClass = classType === privateGroup
    const isOnline = onlineType || "offline";

    const ClassTypeOptions = isOnline === "online" ? onlineClasses : getAvailabilityOptions(classType, onlineType);

    const [selectClassType, setSelectClassType] = useState(
        null
    );

    const [selectedHour, setSelectedHour] = useState(defaultHour)
    const [selectedMinute, setSelectedMinute] = useState(defaultMinute)
    const [selectedAmPm, setSelectedAmPm] = useState(defaultAmpm)

    const [formErrors, setFormErrors] = useState({})

    const resetForm = () => {
        setSelectClassType(null);
        setSelectedHour(defaultHour)
        setSelectedMinute(defaultMinute)
        setSelectedAmPm(defaultAmpm)
        setFormErrors({})
        handleClose?.()
    }

    const handleAddClass = () => {
        if (!selectClassType?.value) {
            setFormErrors((prev) => ({
                ...prev,
                classConflict:
                    "Choose a class first.",
            }))
            return;
        }
        const payload = {
            online_type: selectClassType.value === "Online" ? "online" : "offline",
            time: `${selectedHour?.value}:${selectedMinute?.value} ${selectedAmPm?.value}`,
            type: selectClassType.value === "Online" && isOnline != "Offline" ? "Private" : selectClassType.value,
        }

        const isTimeError = relevantDays.some((day) =>
            isTimeConflictFromRelevantDays(
                day,
                selectedDay,
                payload,
                calculateTimeDifferenceInMinutes,
                classDuration
            )
        );


        if (isTimeError) {
            setFormErrors((prev) => ({
                ...prev,
                classConflict:
                    "Separation between classes is shorter than class duration",
            }))
        } else {
            setFormErrors((prev) => ({...prev, classConflict: ""}))
            onAddClass?.(payload)
            resetForm()
        }
    }

    const modalRef = useRef()

    useEffect(() => {
        if (selectClassType === null && ClassTypeOptions.length == 1) {
            setSelectClassType(ClassTypeOptions[0])
        }
    }, [ClassTypeOptions, show])

    return (
        <>

            <Modal show={show} handleClose={resetForm} maxWidth={515}>
                <Container>
                    <HelperText center>Select type of class</HelperText>
                    <RadioButtonContainer nbrCol={1}>
                        <SelectButton
                            id="class-select"
                            borderColor="lightBlue"
                            valueColor="blue"
                            bgColor="blue15"
                            isDisabled={ClassTypeOptions?.length == 1}
                            showIndicator={ClassTypeOptions?.length != 1}
                            controlledMenu={false}
                            placeholder="Select Class"
                            value={selectClassType}
                            onChange={(e) => {
                                setSelectClassType(e)
                            }}
                            options={ClassTypeOptions}

                        />
                    </RadioButtonContainer>
                    <HelperText center>Select class start time</HelperText>
                    <SelectErrorContainer>
                        <SelectTimeContainer>
                            <SelectInputContainer>
                                <SelectInputVariant
                                    placeholder='HH'
                                    options={hoursOption}
                                    menuTarget={modalRef?.current}
                                    onChange={(hour) => {
                                        setSelectedHour(hour)
                                    }}
                                    value={selectedHour}
                                />
                            </SelectInputContainer>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}>
                                :
                            </div>
                            <SelectInputContainer>
                                <SelectInputVariant
                                    placeholder='MM'
                                    options={minutesOption}
                                    menuTarget={modalRef?.current}
                                    onChange={(minute) => setSelectedMinute(minute)}
                                    value={selectedMinute}
                                />
                            </SelectInputContainer>
                            <SelectInputContainer>
                                <SelectInputVariant
                                    placeholder='AM/PM'
                                    options={ampmOption}
                                    menuTarget={modalRef?.current}
                                    onChange={(ampm) => setSelectedAmPm(ampm)}
                                    value={selectedAmPm}
                                />
                            </SelectInputContainer>
                        </SelectTimeContainer>
                        {formErrors?.classConflict ? (
                            <HelperText color='pink' size='regular'>
                                {formErrors.classConflict}
                            </HelperText>
                        ) : null}
                    </SelectErrorContainer>
                    <ProfileButtonContainer>
                        <ButtonPrimary onClick={handleAddClass}>Add Class</ButtonPrimary>
                        <ButtonPrimary variant='outline' onClick={resetForm}>
                            Cancel
                        </ButtonPrimary>
                    </ProfileButtonContainer>
                </Container>
            </Modal>
            {/* To allow showing the menu outside of the Modal */}
            <div style={{
                zIndex: 99999
            }} ref={modalRef}/>
        </>
    )
}

const HelperText = styled.p`
    text-align: ${({center}) => (center ? "center" : "left")};
    color: ${({color = "txt"}) => themeColor(color)};
    font-size: ${({size = "medium"}) => themeFontSize(size)};
    font-weight: 500;
`

const Container = styled.div`
    display: grid;
    gap: 35px;
`

const RadioButtonContainer = styled.div`
    display: grid;
    gap: 1rem;
    grid-template-columns: ${({nbrCol = 2}) => `repeat(${nbrCol},1fr)`};
`
const SelectTimeContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(16, 1fr);
    gap: 1rem;
`
const SelectInputContainer = styled.div`
    grid-column: span 5;
`

const ProfileButtonContainer = styled.div`
    display: grid;
    gap: 1rem;
`

const SelectErrorContainer = styled.div`
    display: grid;
    gap: 1rem;
`
